import type { Table } from "dexie"
import Dexie, { liveQuery } from "dexie"
import { bumpDbEpoch } from "../db-epoch"
import type { QueryFilter, StorageAdapter } from "../types"

export class DexieAdapter implements StorageAdapter {
  constructor(private db: Dexie) {}

  /**
   * Subscribers to the Dexie `close` event. We fan out from ONE Dexie
   * listener (wired lazily, once) to this Set rather than `db.on("close", fn)`
   * per subscriber: Dexie exposes no documented per-handler unsubscribe for
   * lifecycle events, so a Set + delete is the only reliable way to detach a
   * handler — and it avoids leaking one Dexie listener per SyncEngine restart.
   */
  private closeListeners = new Set<() => void>()
  private closeWired = false

  private table(name: string): Table {
    return this.db.table(name)
  }

  async get<T>(table: string, id: string): Promise<T | null> {
    const result = await this.table(table).get(id)
    return (result as T) ?? null
  }

  async getAll<T>(table: string, filter?: QueryFilter): Promise<T[]> {
    const t = this.table(table)

    if (!filter) {
      return t.toArray() as Promise<T[]>
    }

    const { limit, offset, orderBy, ...conditions } = filter
    let collection = t.toCollection()

    // If we have a single indexed condition, use Dexie's where
    const condEntries = Object.entries(conditions).filter(
      ([, v]) => v !== undefined
    )
    if (condEntries.length === 1) {
      const [key, value] = condEntries[0]!
      collection = t.where(key).equals(value as string | number)
    }

    let items: T[]
    if (condEntries.length > 1) {
      // Multiple conditions: filter manually
      const all = await t.toArray()
      items = all.filter((item) =>
        condEntries.every(
          ([key, value]) => (item as Record<string, unknown>)[key] === value
        )
      ) as T[]
    } else {
      items = (await collection.toArray()) as T[]
    }

    if (orderBy) {
      const desc = orderBy.startsWith("-")
      const field = desc ? orderBy.slice(1) : orderBy
      items.sort((a, b) => {
        const av = (a as Record<string, unknown>)[field]
        const bv = (b as Record<string, unknown>)[field]
        if (av == null || bv == null) return 0
        const cmp = av < bv ? -1 : av > bv ? 1 : 0
        return desc ? -cmp : cmp
      })
    }

    if (offset) items = items.slice(offset)
    if (limit !== undefined) items = items.slice(0, limit)

    return items
  }

  async put<T extends { id: string }>(table: string, item: T): Promise<void> {
    await this.table(table).put(item)
  }

  async putMany<T extends { id: string }>(
    table: string,
    items: T[]
  ): Promise<void> {
    await this.table(table).bulkPut(items)
  }

  async delete(table: string, id: string): Promise<void> {
    await this.table(table).delete(id)
  }

  async clear(table: string): Promise<void> {
    await this.table(table).clear()
  }

  subscribe<T>(
    table: string,
    filter: QueryFilter | undefined,
    cb: (data: T[]) => void
  ): () => void {
    const observable = liveQuery(() => this.getAll<T>(table, filter))
    const subscription = observable.subscribe({
      next: (data) => cb(data),
    })
    return () => subscription.unsubscribe()
  }

  /**
   * Run `fn` inside a Dexie `rw` transaction scoped to `tables`. Used for
   * read-modify-write flows that need atomicity vs concurrent callers (e.g.
   * partner.clientGroupIds membership re-set). Dexie serializes overlapping
   * transactions on the same tables so a second call's get returns the
   * post-write snapshot from the first.
   */
  async transaction<R>(tables: string[], fn: () => Promise<R>): Promise<R> {
    const dexieTables = tables.map((t) => this.table(t))
    return this.db.transaction("rw", dexieTables, fn)
  }

  /**
   * Dexie fires `close` when the connection is shut — including the close
   * triggered when another tab deletes or upgrades the database. `db.ts`
   * registers a `versionchange` handler that closes this connection on both
   * (and reloads on an upgrade); this `close` fan-out is what wakes
   * `SyncEngine.recoverFromUnexpectedClose` on the delete/logout path.
   */
  onClose(cb: () => void): () => void {
    this.closeListeners.add(cb)
    if (!this.closeWired) {
      this.closeWired = true
      this.db.on("close", () => {
        for (const listener of this.closeListeners) listener()
      })
    }
    return () => {
      this.closeListeners.delete(cb)
    }
  }

  async reopen(): Promise<void> {
    if (this.db.isOpen()) return
    await this.db.open()
    // The connection is fresh, but every `liveQuery` observer created while it
    // was closed is still bound to the dead one and will never re-fire. Bump
    // the DB epoch so the reactive read layer (dexie-reactive +
    // use-shared-live-query) tears down and re-subscribes on this connection —
    // without it the UI stays blank until a manual reload.
    bumpDbEpoch()
  }
}
